import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { MessageCircle, Send, Clock, CheckCircle, AlertCircle, XCircle, Store, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const statusConfig = {
  open: { label: 'Aberto', color: 'bg-blue-500', icon: Clock },
  in_progress: { label: 'Em Andamento', color: 'bg-yellow-500', icon: AlertCircle },
  resolved: { label: 'Resolvido', color: 'bg-green-500', icon: CheckCircle },
  closed: { label: 'Fechado', color: 'bg-gray-500', icon: XCircle }
};

const priorityConfig = {
  low: { label: 'Baixa', color: 'text-gray-400', bg: 'bg-gray-500/20' },
  normal: { label: 'Normal', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  high: { label: 'Alta', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  urgent: { label: 'Urgente', color: 'text-red-400', bg: 'bg-red-500/20' }
};

const AdminSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (selectedTicket) {
      fetchTicketDetails(selectedTicket);
    }
  }, [selectedTicket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticketDetails?.messages]);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/support/tickets`);
      setTickets(res.data);
    } catch (e) {
      toast.error('Erro ao carregar tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/support/tickets/${ticketId}`);
      setTicketDetails(res.data);
    } catch (e) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      await axios.post(`${API}/support/tickets/${selectedTicket}/messages`, {
        message: newMessage
      });
      setNewMessage('');
      fetchTicketDetails(selectedTicket);
      fetchTickets(); // Update status in list
    } catch (e) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await axios.put(`${API}/support/tickets/${selectedTicket}/status?status=${status}`);
      toast.success('Status atualizado');
      fetchTicketDetails(selectedTicket);
      fetchTickets();
    } catch (e) {
      toast.error('Erro ao atualizar status');
    }
  };

  const filteredTickets = statusFilter === 'all' 
    ? tickets 
    : tickets.filter(t => t.status === statusFilter);

  const openTicketsCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#ff6b00] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Suporte aos Lojistas</h1>
          <p className="text-white/60">Gerencie os tickets de suporte</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge className="bg-blue-500/20 text-blue-400 px-3 py-1">
            {openTicketsCount} abertos
          </Badge>
          <Badge className="bg-yellow-500/20 text-yellow-400 px-3 py-1">
            {inProgressCount} em andamento
          </Badge>
        </div>
      </div>

      <div className="flex h-[calc(100vh-200px)] gap-4">
        {/* Tickets List */}
        <div className="w-96 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-white/40" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-[#13131f] border-white/10 text-white">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent className="bg-[#13131f] border-white/10">
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="open">Abertos</SelectItem>
                <SelectItem value="in_progress">Em Andamento</SelectItem>
                <SelectItem value="resolved">Resolvidos</SelectItem>
                <SelectItem value="closed">Fechados</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 overflow-y-auto space-y-2">
            {filteredTickets.length === 0 ? (
              <Card className="bg-[#13131f] border-white/10 p-6 text-center">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-white/20" />
                <p className="text-white/60">Nenhum ticket encontrado</p>
              </Card>
            ) : (
              filteredTickets.map(ticket => (
                <Card
                  key={ticket.id}
                  className={`bg-[#13131f] border-white/10 p-3 cursor-pointer transition-all hover:border-[#ff6b00]/50 ${
                    selectedTicket === ticket.id ? 'border-[#ff6b00]' : ''
                  }`}
                  onClick={() => setSelectedTicket(ticket.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${priorityConfig[ticket.priority]?.bg}`}>
                      <Store className={`w-4 h-4 ${priorityConfig[ticket.priority]?.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{ticket.subject}</p>
                      <p className="text-sm text-white/60 truncate">{ticket.store_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`${statusConfig[ticket.status]?.color} text-white text-xs`}>
                          {statusConfig[ticket.status]?.label}
                        </Badge>
                        <span className="text-xs text-white/40">
                          {new Date(ticket.updated_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <Card className="flex-1 bg-[#13131f] border-white/10 flex flex-col">
          {selectedTicket && ticketDetails ? (
            <>
              {/* Header */}
              <CardHeader className="border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">{ticketDetails.ticket.subject}</CardTitle>
                    <p className="text-sm text-white/60">
                      <Store className="w-3 h-3 inline mr-1" />
                      {ticketDetails.ticket.store_name} • {ticketDetails.ticket.user_email}
                    </p>
                  </div>
                  <Select
                    value={ticketDetails.ticket.status}
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger className={`w-40 ${statusConfig[ticketDetails.ticket.status]?.color} text-white border-0`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#13131f] border-white/10">
                      <SelectItem value="open">Aberto</SelectItem>
                      <SelectItem value="in_progress">Em Andamento</SelectItem>
                      <SelectItem value="resolved">Resolvido</SelectItem>
                      <SelectItem value="closed">Fechado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                {ticketDetails.messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_role === 'super_admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-3 ${
                        msg.sender_role === 'super_admin'
                          ? 'bg-[#ff6b00]/20 text-white'
                          : 'bg-blue-500/20 text-white'
                      }`}
                    >
                      <p className="text-xs text-white/60 mb-1">
                        {msg.sender_role === 'super_admin' ? '👨‍💼 Você' : `👤 ${msg.sender_name}`} • 
                        {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <p className="whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </CardContent>

              {/* Input */}
              <div className="p-4 border-t border-white/10">
                <div className="flex gap-2">
                  <Input
                    placeholder="Digite sua resposta..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="bg-[#ff6b00] hover:bg-[#ff8533]"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-white/40">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p>Selecione um ticket para responder</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminSupport;
